import { Helmet } from 'react-helmet-async';

const siteUrl = 'https://akiva.com';

// Organization schema
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'AKIVA',
  alternateName: 'AKIVA',
  url: siteUrl,
  logo: `${siteUrl}/akiva-logo.png`,
  description: 'AKIVA - AI Engineering & Strategy Consulting. AI-native engineering firm specializing in enterprise services, domain-specific solutions, and high-performance infrastructure from strategy through deployment. We build innovative systems that drive measurable results for commercial enterprises and government agencies.',
  foundingDate: '2017',
  sameAs: [
    'https://www.linkedin.com/company/akiva-ai',
    'https://github.com/akiva-ai',
    'https://www.youtube.com/@akiva-ai',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'info@akiva.com',
    contactType: 'customer service',
    availableLanguage: 'English',
  },
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'US',
  },
};

// Service schema generator
export const createServiceSchema = (service: {
  name: string;
  description: string;
  url: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: service.name,
  description: service.description,
  provider: organizationSchema,
  url: `${siteUrl}${service.url}`,
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  serviceType: 'AI Engineering & Strategy Consulting',
});

// Product schema generator
export const createProductSchema = (product: {
  name: string;
  description: string;
  url: string;
  image?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  url: `${siteUrl}${product.url}`,
  image: product.image ? `${siteUrl}${product.image}` : undefined,
  brand: {
    '@type': 'Brand',
    name: 'AKIVA',
  },
  manufacturer: organizationSchema,
});

// FAQ schema generator
export const createFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

// Breadcrumb schema generator
export const createBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${siteUrl}${item.url}`,
  })),
});

// WebSite schema. NOTE: removed potentialAction.SearchAction — the site has
// no /search route, and Google was following the declaration to crawl
// `/search?q={search_term_string}` literally (GSC: "Crawled, currently not
// indexed" 2026-04-27). Re-add only if a real /search page is implemented.
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'AKIVA',
  url: siteUrl,
};

// Professional Service schema
export const professionalServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'AKIVA',
  image: `${siteUrl}/akiva-logo.png`,
  url: siteUrl,
  email: 'info@akiva.com',
  priceRange: '$$$',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'US',
  },
  // No geo coordinates and no openingHoursSpecification: we publish no verified
  // street address, and coordinates without a matching address are a structured
  // data inconsistency that Rich Results flags. Add both together, or neither.
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  sameAs: [
    'https://www.linkedin.com/company/akiva-ai',
    'https://github.com/akiva-ai',
  ],
};

interface StructuredDataProps {
  type: 'organization' | 'service' | 'product' | 'faq' | 'breadcrumb' | 'website' | 'professional';
  data?: any;
}

const StructuredData = ({ type, data }: StructuredDataProps) => {
  let schema;

  switch (type) {
    case 'organization':
      schema = organizationSchema;
      break;
    case 'website':
      schema = websiteSchema;
      break;
    case 'professional':
      schema = professionalServiceSchema;
      break;
    case 'service':
      schema = createServiceSchema(data);
      break;
    case 'product':
      schema = createProductSchema(data);
      break;
    case 'faq':
      schema = createFAQSchema(data);
      break;
    case 'breadcrumb':
      schema = createBreadcrumbSchema(data);
      break;
    default:
      schema = organizationSchema;
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export default StructuredData;
